import { Text } from '../Text'
import { DISTRIBUTION_TIERS, formatDealsInvolved, formatHireDate, RatingChip, VsOverall } from './shared'

export interface ParticipantProfileForHeader {
	firstName: string | null
	lastName: string | null
	email: string
	jobTitle: string | null
	hireDate: string | null
	manager: { firstName: string | null; lastName: string | null } | null
	cycles: unknown[]
	completionRatePercent: number | null
	avgRating: number | null
	avgVsOverall: number | null
	highestRatedDeal: { score: number; name: string } | null
	lowestRatedDeal: { score: number; name: string } | null
}

function getInitials(firstName: string | null, lastName: string | null, email: string): string {
	const initials = [firstName?.[0], lastName?.[0]].filter(Boolean).join('').toUpperCase()
	if (initials) return initials
	return email.slice(0, 2).toUpperCase()
}

function getTierForRating(rating: number | null, scaleMax: number) {
	if (rating === null) return null
	const normalized = scaleMax === 10 ? rating : (rating * 10) / scaleMax
	if (normalized <= 2) return DISTRIBUTION_TIERS[0]
	if (normalized <= 4) return DISTRIBUTION_TIERS[1]
	if (normalized <= 6) return DISTRIBUTION_TIERS[2]
	if (normalized <= 8) return DISTRIBUTION_TIERS[3]
	return DISTRIBUTION_TIERS[4]
}

function CompletionRateChip({ value }: Readonly<{ value: number | null }>) {
	if (value === null) return <span className='text-sm text-neutral-400'>—</span>
	let className = 'bg-red-100 text-red-600'
	if (value >= 90) className = 'bg-emerald-100 text-emerald-600'
	else if (value >= 70) className = 'bg-blue-100 text-blue-600'
	else if (value >= 50) className = 'bg-orange-100 text-orange-600'
	return (
		<span
			className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${className}`}>
			{value}%
		</span>
	)
}

function StatLabel({ children }: Readonly<{ children: React.ReactNode }>) {
	return <span className='text-xs text-neutral-500'>{children}</span>
}

export function ProfileHeaderCard({
	profile,
	scaleMax = 10,
}: Readonly<{
	profile: ParticipantProfileForHeader
	scaleMax?: number
}>) {
	const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || 'Unnamed Participant'
	const tier = getTierForRating(profile.avgRating, scaleMax) ?? {
		color: '#E5E7EB',
		textColor: '#4B5563',
	}
	const initials = getInitials(profile.firstName, profile.lastName, profile.email)
	const managerName = profile.manager
		? [profile.manager.firstName, profile.manager.lastName].filter(Boolean).join(' ') || '—'
		: '—'

	return (
		<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-6 text-left'>
			<div className='flex items-center gap-4'>
				<div
					className='h-14 w-14 rounded-full flex items-center justify-center text-base font-semibold shrink-0'
					style={{ backgroundColor: tier.color, color: tier.textColor }}
					aria-hidden='true'>
					{initials}
				</div>
				<div className='flex flex-col gap-0.5'>
					<Text variant='large' weight='semibold'>
						{fullName}
					</Text>
					<Text variant='small' className='text-neutral-500'>
						{profile.jobTitle ?? profile.email}
					</Text>
				</div>
			</div>

			<div className='grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 border-t border-neutral-100 pt-5'>
				<div className='flex flex-col gap-1.5'>
					<StatLabel>Date of hire</StatLabel>
					<span className='text-sm font-semibold text-neutral-900'>{formatHireDate(profile.hireDate)}</span>
				</div>
				<div className='flex flex-col gap-1.5'>
					<StatLabel>Manager</StatLabel>
					<span className='text-sm font-semibold text-neutral-900'>{managerName}</span>
				</div>
				<div className='flex flex-col gap-1.5'>
					<StatLabel>Deals Involved</StatLabel>
					<span className='text-sm font-semibold text-neutral-900'>{formatDealsInvolved(profile.cycles.length)}</span>
				</div>
				<div className='flex flex-col gap-1.5'>
					<StatLabel>Completion Rate</StatLabel>
					<CompletionRateChip value={profile.completionRatePercent} />
				</div>

				<div className='flex flex-col gap-1.5'>
					<StatLabel>Average Rating</StatLabel>
					<div>
						<RatingChip value={profile.avgRating} scaleMax={scaleMax} />
					</div>
				</div>
				<div className='flex flex-col gap-1.5'>
					<StatLabel>Average vs. Overall</StatLabel>
					<VsOverall value={profile.avgVsOverall} />
				</div>
				<div className='flex flex-col gap-1.5'>
					<StatLabel>Highest Rated Deal</StatLabel>
					{profile.highestRatedDeal ? (
						<div className='flex items-center gap-2'>
							<RatingChip value={profile.highestRatedDeal.score} scaleMax={scaleMax} />
							<span className='text-sm font-semibold text-neutral-900 truncate'>{profile.highestRatedDeal.name}</span>
						</div>
					) : (
						<span className='text-sm text-neutral-400'>—</span>
					)}
				</div>
				<div className='flex flex-col gap-1.5'>
					<StatLabel>Lowest Rated Deal</StatLabel>
					{profile.lowestRatedDeal ? (
						<div className='flex items-center gap-2'>
							<RatingChip value={profile.lowestRatedDeal.score} scaleMax={scaleMax} />
							<span className='text-sm font-semibold text-neutral-900 truncate'>{profile.lowestRatedDeal.name}</span>
						</div>
					) : (
						<span className='text-sm text-neutral-400'>—</span>
					)}
				</div>
			</div>
		</div>
	)
}
